/**
 * What the organisation is and where it is going, in its own words. Every workbench shows it at the
 * centre of its rings; administrators write it here.
 */

import { useState } from 'react';

import { routes } from '../api/client.ts';
import { timeAgo, useLoad } from '../hooks.ts';
import { useMay } from '../permissions.ts';
import { SubjectName } from './SubjectPicker.tsx';
import { Button, ErrorNote, inputClass, ListBlock, ListGroup, ListRow, StatusMark } from './ui.tsx';
import { Markdown } from './Markdown.tsx';

export function OrganisationProfilePanel() {
  const profile = useLoad(() => routes.organisationProfile(), []);
  // Update on the organisation's settings (today: administrators), from what the person may do.
  const admin = { data: useMay('organisation.settings', 'update') };
  const [draft, setDraft] = useState<{ description: string; vision: string }>();
  const [error, setError] = useState<string>();
  const [saved, setSaved] = useState(false);
  const p = profile.data;
  const d = draft ?? { description: p?.description ?? '', vision: p?.vision ?? '' };
  const save = async () => {
    setError(undefined);
    try {
      await routes.setOrganisationProfile({ description: d.description, vision: d.vision });
      setDraft(undefined);
      setSaved(true);
      profile.reload();
    } catch (e) {
      setError((e as Error).message);
    }
  };
  const changed = p?.updatedAt ? (
    <>
      Last changed {timeAgo(p.updatedAt)}
      {p.updatedBy ? (
        <>
          {' by '}
          <SubjectName id={p.updatedBy} fallback={p.updatedBy} />
        </>
      ) : null}
      .
    </>
  ) : null;
  return (
    <ListGroup id="profile" title="What we are, and where we are going" description="Every workbench in the organisation shows this at the centre of its rings.">
      {admin.data ? (
        <>
          <ListRow title="What the organisation is" htmlFor="aioe-org-description">
            <textarea id="aioe-org-description" className={`${inputClass} min-h-16`} maxLength={2000} value={d.description} onChange={(e) => (setSaved(false), setDraft({ ...d, description: e.target.value }))} placeholder="We build AI workbenches for regulated teams." />
          </ListRow>
          <ListRow title="Where it is going" htmlFor="aioe-org-vision">
            <textarea id="aioe-org-vision" className={`${inputClass} min-h-16`} maxLength={2000} value={d.vision} onChange={(e) => (setSaved(false), setDraft({ ...d, vision: e.target.value }))} placeholder="Every team governs its own agents." />
          </ListRow>
          <ListRow
            title={saved ? <StatusMark state="connected" label="Saved; workbenches show it from their next refresh" /> : <span className="font-normal text-muted">{changed ?? 'Not written yet.'}</span>}
            control={
              <Button size="sm" isDisabled={!draft} onPress={() => void save()}>
                Save
              </Button>
            }
          />
        </>
      ) : (
        <ListBlock>
          <Markdown text={p?.description} fallback={<p>Not written yet.</p>} />
          <Markdown className="text-muted" text={p?.vision} />
          {changed ? <p className="mt-2 text-xs text-muted">{changed}</p> : null}
        </ListBlock>
      )}
      {profile.error || error ? (
        <ListBlock>
          <ErrorNote error={profile.error ?? error} />
        </ListBlock>
      ) : null}
    </ListGroup>
  );
}
